import { ErrorModal, getMomentTz, HelmetDexteel } from "@dexteel/mesf-core";
import { Grid, Paper, Typography } from "@mui/material";
import { GridApi } from "ag-grid-community";
import { Moment } from "moment";
import { useState } from "react";
import { ExcelButton } from "../../../controls/buttons/ExcelButton";
import { isMobileDevice } from "../../../utils/responsive-utils";
import { useResize } from "../../../utils/useResize";
import { exportToExcel } from "../../../utils/excelExportOptions";
import { useDelaysReportQuery } from "../hooks/queries/useDelaysReportQuery";
import { useRealtimeInvalidation } from "../hooks/queries/useRealtimeInvalidation";
import { DelaysReportTable } from "./components/DelaysReportTable";
import { Filters } from "./components/filters/Filters";

const moment = getMomentTz();

const assetTitles: { [key: number]: string } = {
  11: "EAF Delays Report",
  12: "LMF Delays Report",
  13: "Caster Delays Report",
};

export const DelaysReportPage = ({ assetId }: { assetId: number }) => {
  const { width } = useResize();
  const isMobile = isMobileDevice(width);

  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const [error, setError] = useState("");
  const [isExporting, setIsExporting] = useState(false);

  // Enable real-time updates via SignalR
  useRealtimeInvalidation();

  const [filterStartDate, setFilterStartDate] = useState<Moment>(
    moment().startOf("day"),
  );
  const [filterEndDate, setFilterEndDate] = useState<Moment>(
    moment().endOf("day"),
  );
  const [includeMicrodelays, setIncludeMicrodelays] = useState<boolean>(false);

  const { data: delays = [], isLoading } = useDelaysReportQuery(
    filterStartDate.toDate(),
    filterEndDate.toDate(),
    assetId,
    includeMicrodelays,
    (err) => setError(err.message),
  );

  const handleFilterChange = (startDate: Moment, endDate: Moment) => {
    setFilterStartDate(startDate);
    setFilterEndDate(endDate);
  };

  const handleIncludeMicrodelaysChange = (include: boolean) => {
    setIncludeMicrodelays(include);
  };

  const handleExportToExcel = () => {
    if (gridApi) {
      setIsExporting(true);
      try {
        exportToExcel({
          gridApi,
          fileName: "delays_report",
          sheetName: "Delays Report",
          startDate: filterStartDate.toDate(),
          endDate: filterEndDate.toDate(),
          processCellCallback: (
            cellParams: import("ag-grid-community").ProcessCellForExportParams,
          ) => {
            const colId = cellParams.column.getColId();
            const val = cellParams.value;

            if (
              colId === "DelayCategory" &&
              val &&
              typeof val === "object" &&
              "name" in val
            ) {
              return (val as { name: string }).name ?? "";
            }

            return val ?? "";
          },
          additionalAppendContent: [
            {
              label: "Total Records:",
              value: (delays?.length || 0).toString(),
            },
          ],
        });
      } catch (exportError) {
        setError(
          "An error occurred while exporting to Excel. Please try again.",
        );
      } finally {
        setIsExporting(false);
      }
    } else {
      setError("Unable to export data. Please try again.");
    }
  };

  return (
    <>
      <HelmetDexteel title={`${assetTitles[assetId]}`} />
      <Grid container spacing={2} sx={{ p: 2 }}>
        <Grid item xs={12}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {assetTitles[assetId] || "Delays Report"}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }} elevation={2}>
            <Grid
              container
              spacing={2}
              alignItems="center"
              justifyContent="space-between"
            >
              <Grid
                item
                container
                xs={12}
                md={6}
                spacing={1}
                alignItems="center"
              >
                <Filters
                  onFilterChange={handleFilterChange}
                  initialStartDate={filterStartDate}
                  initialEndDate={filterEndDate}
                  includeMicrodelays={includeMicrodelays}
                  onIncludeMicrodelaysChange={handleIncludeMicrodelaysChange}
                />
              </Grid>

              {!isMobile && (
                <Grid item xs={12} md={2} container justifyContent="flex-end">
                  <ExcelButton
                    handleExportToExcel={handleExportToExcel}
                    disabled={
                      !gridApi ||
                      isLoading ||
                      isExporting ||
                      !delays ||
                      delays.length === 0
                    }
                    label={isExporting ? "EXPORTING..." : "EXPORT TO EXCEL"}
                  />
                </Grid>
              )}
            </Grid>
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <DelaysReportTable
            key={`${assetId}`}
            isLoading={isLoading}
            rows={delays || []}
            setGridApi={setGridApi}
          />
        </Grid>
      </Grid>
      <ErrorModal error={error} onHide={() => setError("")} />
    </>
  );
};
